import * as React from 'react';
import { type ReactElement } from 'react';
import { CameraImage } from '../Cameras/CameraImage';
import type { Camera } from '../Cameras/Cameras.types';
import { DndHorizontalList } from '../DndHorizontalList/DndHorizontalList';
import { Icon } from '../Icon/Icon';
import { LoadingSpinner } from '../LoadingSpinner';

export interface IFavoriteCamerasPanelProps {
    region: string;
    cameraRoot: number;
    allCamerasLink: string;
    noImageUrl: string;
}
interface IFavoriteCamerasPanelState {
    cameras: Camera[];
    open: boolean;
    loading: boolean;
}

export class FavoriteCamerasPanel extends React.Component<IFavoriteCamerasPanelProps, IFavoriteCamerasPanelState> {
    initiated = false;
    constructor(props: IFavoriteCamerasPanelProps) {
        super(props);
        this.state = {
            cameras: null,
            open: false,
            loading: false,
        };
    }

    private loadCameras() {
        fetch('/api/trafficcameras/action/getbyarea?camerapage=' + this.props.cameraRoot)
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }
            })
            .then((data) => {
                const favorited = this.markFavorites(data);
                const myFavorites = favorited.filter((c) => c.isFavorite);
                this.initiated = true;
                this.setState({
                    cameras: myFavorites,
                    loading: false,
                });
            });
    }

    test = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.currentTarget.blur();
    };

    public render(): JSX.Element {
        const myFacoriteCameras = this.state.cameras;
        return (
            <div className="myfavorite-camera-panel__panel-wrapper">
                <button
                    className="btn myfavorite-camera-panel__button"
                    type="button"
                    onClick={this.toggleHidden.bind(this)}
                    data-bs-toggle="collapse"
                    data-bs-target="#my-favorite-cameras-content"
                    aria-expanded="false"
                    aria-controls="my-favorite-cameras-content"
                >
                    <span className="myfavorite-camera-panel__icon">
                        <Icon symbol="icon-camera" />
                    </span>
                    <span className="myfavorite-camera-panel__text">Mina kameror</span>
                    <i
                        className={this.state.open ? 'icon-animated icon-up-open icon-rotate-180' : 'icon-animated icon-up-open'}
                        title={this.state.open ? 'Markör öppen' : 'Markör stängd'}
                    />
                </button>
                <div className="collapse" id="my-favorite-cameras-content">
                    {myFacoriteCameras === null || myFacoriteCameras.length > 0
                        ? this.renderCamperaPanel(myFacoriteCameras, this.props.allCamerasLink)
                        : this.renderNofavoriteCameras(this.props.allCamerasLink)}
                </div>
            </div>
        );
    }

    private toggleHidden() {
        if (!this.initiated) {
            this.loadCameras();
            this.setState({
                loading: true,
                open: !this.state.open,
            });
        } else {
            this.setState({
                open: !this.state.open,
            });
        }
    }

    private renderCamperaPanel = (cameras: Camera[], allCamerasLink: string) => {
        return (
            <>
                <div className="myfavorite-camera-panel__container container-fluid">
                    <div className="row justify-content-center justify-content-sm-between myfavorite-camera-panel__camera-wrapper">
                        {this.initiated &&
                            (this.state.loading ? (
                                <LoadingSpinner />
                            ) : (
                                <DndHorizontalList
                                    disableDraggableFocus={true}
                                    localStorageKey={'MyCameraPages' + this.props.region.toUpperCase()}
                                    containerClass="myfavorite-camera-panel__dnd-container"
                                    childClass="myfavorite-camera-panel__camera-item"
                                >
                                    {cameras.map(this.renderItem.bind(this))}
                                </DndHorizontalList>
                            ))}
                    </div>
                    {this.renderAllCamerasLink(allCamerasLink)}
                </div>
            </>
        );
    };
    private renderItem(item: Camera): ReactElement {
        return (
            <a key={item.pageId} className="myfavorite-camera-panel__camera-link" href={item.url}>
                <div className="myfavorite-camera-panel__camera-image">
                    <CameraImage id={item.pageId} imageUrl={item.imageUrl} noImageUrl={this.props.noImageUrl} title={item.name} updateTime={30000} />
                </div>
                <h4 className="myfavorite-camera-panel__camera-name">{item.name}</h4>
            </a>
        );
    }
    private renderNofavoriteCameras(allCamerasLink: string) {
        return (
            <div className="section myfavorite-camera-panel__container">
                <div className="container">
                    <div className="row myfavorite-camera-panel__camera-wrapper justify-content-center">
                        <div className="myfavorite-camera-panel__no-favorite-cameras d-flex align-items-center">
                            <p>Här visas dina favoritkameror. Lägg till kameror från kartan eller sidan alla kameror. </p>
                        </div>
                    </div>
                    {this.renderAllCamerasLink(allCamerasLink)}
                </div>
            </div>
        );
    }
    private renderAllCamerasLink(allCamerasLink: string): JSX.Element {
        return (
            <div className="row justify-content-end">
                <a className="myfavorite-camera-panel__link" href={allCamerasLink}>
                    <span className="myfavorite-camera-panel__text"> Se alla kameror</span>
                    <span className="icon-up-open icon-rotate-90"></span>
                </a>
            </div>
        );
    }

    private markFavorites(cameras: Camera[]): Camera[] {
        const favorites = this.getFavoriteCameras();
        const result: Camera[] = cameras.map((c) => {
            c.isFavorite = favorites.indexOf(c.pageId) > -1;
            return c;
        });
        return result;
    }

    private getFavoriteCameras(): number[] {
        const key = 'MyCameraPages' + this.props.region.toUpperCase();
        const ids = localStorage[key] ? (JSON.parse(localStorage[key]) as string[]) : [];

        return ids.reduce((total, current) => {
            const id = Number(current);

            if (!isNaN(id) && id !== 0) {
                total.push(id);
            }
            return total;
        }, []);
    }
}
